import { useRef, useEffect } from "react";

/**
 * Configuration for the useNumberFieldKeys hook
 */
export type UseNumberFieldKeysConfig = {
  /** Function to increment the value by a specified amount */
  increment: (amount: number) => void;
  /** Function to decrement the value by a specified amount */
  decrement: (amount: number) => void;
  /** Function to set the value to the minimum allowed value */
  setToMin: () => void;
  /** Function to set the value to the maximum allowed value */
  setToMax: () => void;
  /** The step value for increment/decrement operations */
  step: number;
  /** Multiplier for large step operations (PageUp/PageDown) */
  largeStepMultiplier: number;
};

/**
 * Custom hook that handles keyboard interactions for number fields.
 *
 * This hook provides keyboard navigation support for number input fields:
 * - Arrow Up/Down: Increment/decrement by step value
 * - Page Up/Down: Increment/decrement by step * largeStepMultiplier
 * - Home: Set to minimum value
 * - End: Set to maximum value
 *
 * @param config - Configuration object containing increment/decrement functions and step values
 * @returns Object containing the input ref to attach to the input element
 *
 * @example
 * const { inputRef } = useNumberFieldKeys({
 *   increment: (amount) => setValue(prev => prev + amount),
 *   decrement: (amount) => setValue(prev => prev - amount),
 *   setToMin: () => setValue(minValue),
 *   setToMax: () => setValue(maxValue),
 *   step: 1,
 *   largeStepMultiplier: 10
 * });
 */
function useNumberFieldKeys(config: UseNumberFieldKeysConfig) {
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle keyboard interactions (Up/Down arrows, PageUp/PageDown, Home/End)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case "ArrowUp":
          event.preventDefault();
          config.increment(config.step);
          break;
        case "ArrowDown":
          event.preventDefault();
          config.decrement(config.step);
          break;
        case "PageUp":
          event.preventDefault();
          config.increment(config.step * config.largeStepMultiplier);
          break;
        case "PageDown":
          event.preventDefault();
          config.decrement(config.step * config.largeStepMultiplier);
          break;
        case "Home":
          event.preventDefault();
          config.setToMin();
          break;
        case "End":
          event.preventDefault();
          config.setToMax();
          break;
        default:
          break;
      }
    };

    const inputElement = inputRef.current;
    if (inputElement) {
      inputElement.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      if (inputElement) {
        inputElement.removeEventListener("keydown", handleKeyDown);
      }
    };
  }, [config]);

  return {
    inputRef,
  };
}

export default useNumberFieldKeys;
